import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calculator, Clock, Droplets, FlaskConical, AlertTriangle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface CalculationResult {
  value: number;
  unit: string;
  formula: string;
  explanation: string;
}

export const InfusionCalculator = () => {
  // Infusionszeit berechnen
  const [volume, setVolume] = useState<string>('');
  const [rate, setRate] = useState<string>('');
  const [timeResult, setTimeResult] = useState<CalculationResult | null>(null);

  // Infusionsrate berechnen
  const [volumeForRate, setVolumeForRate] = useState<string>('');
  const [time, setTime] = useState<string>('');
  const [timeUnit, setTimeUnit] = useState<string>('hours');
  const [rateResult, setRateResult] = useState<CalculationResult | null>(null);

  // Tropfrate berechnen
  const [volumeForDrops, setVolumeForDrops] = useState<string>('');
  const [timeForDrops, setTimeForDrops] = useState<string>('');
  const [dropFactor, setDropFactor] = useState<string>('20');
  const [dropResult, setDropResult] = useState<CalculationResult | null>(null);

  // Volumen berechnen
  const [rateForVolume, setRateForVolume] = useState<string>('');
  const [timeForVolume, setTimeForVolume] = useState<string>('');
  const [timeUnitForVolume, setTimeUnitForVolume] = useState<string>('hours');
  const [volumeResult, setVolumeResult] = useState<CalculationResult | null>(null);

  // Infusionszeit berechnen
  const calculateTime = () => {
    const vol = parseFloat(volume);
    const rt = parseFloat(rate);
    
    if (vol > 0 && rt > 0) {
      const timeInHours = vol / rt;
      const hours = Math.floor(timeInHours);
      const minutes = Math.round((timeInHours - hours) * 60);
      
      setTimeResult({
        value: timeInHours,
        unit: `${hours}h ${minutes}min`,
        formula: 'Zeit = Volumen ÷ Rate',
        explanation: `${vol} ml ÷ ${rt} ml/h = ${timeInHours.toFixed(2)} Stunden`
      });
    }
  };

  // Infusionsrate berechnen
  const calculateRate = () => {
    const vol = parseFloat(volumeForRate);
    const t = parseFloat(time);
    
    if (vol > 0 && t > 0) {
      const timeInHours = timeUnit === 'minutes' ? t / 60 : t;
      const calculatedRate = vol / timeInHours;
      
      setRateResult({
        value: calculatedRate,
        unit: 'ml/h',
        formula: 'Rate = Volumen ÷ Zeit',
        explanation: `${vol} ml ÷ ${timeInHours} h = ${calculatedRate.toFixed(1)} ml/h`
      });
    }
  };

  // Tropfrate berechnen
  const calculateDropRate = () => {
    const vol = parseFloat(volumeForDrops);
    const t = parseFloat(timeForDrops);
    const factor = parseFloat(dropFactor);
    
    if (vol > 0 && t > 0 && factor > 0) {
      const dropsPerMinute = (vol * factor) / (t * 60);
      
      setDropResult({
        value: dropsPerMinute,
        unit: 'Tropfen/min',
        formula: 'Tropfen/min = (Volumen × Tropffaktor) ÷ (Zeit × 60)',
        explanation: `(${vol} ml × ${factor} Tropfen/ml) ÷ (${t} h × 60 min) = ${dropsPerMinute.toFixed(1)} Tropfen/min`
      });
    }
  };

  // Volumen berechnen
  const calculateVolume = () => {
    const rt = parseFloat(rateForVolume);
    const t = parseFloat(timeForVolume);
    
    if (rt > 0 && t > 0) {
      const timeInHours = timeUnitForVolume === 'minutes' ? t / 60 : t;
      const calculatedVolume = rt * timeInHours;
      
      setVolumeResult({
        value: calculatedVolume,
        unit: 'ml',
        formula: 'Volumen = Rate × Zeit',
        explanation: `${rt} ml/h × ${timeInHours} h = ${calculatedVolume.toFixed(1)} ml`
      });
    }
  };

  const ResultCard = ({ result, title, icon: Icon }: { result: CalculationResult | null, title: string, icon: any }) => {
    if (!result) return null;
    
    return (
      <Card className="mt-4 border-l-4 border-l-blue-500 shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Icon className="h-5 w-5 text-blue-600" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="text-2xl font-bold text-blue-600">
              {typeof result.value === 'number' ? result.value.toFixed(2) : result.value} {result.unit}
            </div>
            <div className="text-sm text-gray-600">
              <strong>Formel:</strong> {result.formula}
            </div>
            <div className="text-sm text-gray-700 bg-blue-50 p-3 rounded-md">
              <strong>Berechnung:</strong> {result.explanation}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-900 flex items-center justify-center gap-3">
          <Calculator className="h-10 w-10 text-blue-600" />
          Infusions-Rechner
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Professionelles Berechnungstool für die Infusionstherapie in der Pflege
        </p>
        <Badge variant="secondary" className="text-sm">
          Erstellt durch: Krankenbruder.ch
        </Badge>
      </div>

      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Wichtiger Hinweis:</strong> Dieses Tool dient als Berechnungshilfe. Alle Ergebnisse müssen durch qualifiziertes Fachpersonal überprüft werden. Bei Unsicherheiten konsultieren Sie immer einen Arzt oder Apotheker.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="time" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="time" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Infusionszeit
          </TabsTrigger>
          <TabsTrigger value="rate" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Infusionsrate
          </TabsTrigger>
          <TabsTrigger value="drops" className="flex items-center gap-2">
            <Droplets className="h-4 w-4" />
            Tropfrate
          </TabsTrigger>
          <TabsTrigger value="volume" className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4" />
            Volumen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="time">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Infusionszeit berechnen
              </CardTitle>
              <CardDescription>
                Berechnen Sie die benötigte Zeit für eine Infusion bei gegebenem Volumen und Rate
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="volume">Volumen (ml)</Label>
                  <Input
                    id="volume"
                    type="number"
                    placeholder="z.B. 500"
                    value={volume}
                    onChange={(e) => setVolume(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate">Infusionsrate (ml/h)</Label>
                  <Input
                    id="rate"
                    type="number"
                    placeholder="z.B. 125"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={calculateTime} className="w-full" disabled={!volume || !rate}>
                Zeit berechnen
              </Button>
              <ResultCard result={timeResult} title="Berechnete Infusionszeit" icon={Clock} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rate">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-600" />
                Infusionsrate berechnen
              </CardTitle>
              <CardDescription>
                Berechnen Sie die benötigte Infusionsrate bei gegebenem Volumen und Zeit
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="volumeForRate">Volumen (ml)</Label>
                  <Input
                    id="volumeForRate"
                    type="number"
                    placeholder="z.B. 1000"
                    value={volumeForRate}
                    onChange={(e) => setVolumeForRate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time">Zeit</Label>
                  <Input
                    id="time"
                    type="number"
                    placeholder="z.B. 8"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeUnit">Zeiteinheit</Label>
                  <Select value={timeUnit} onValueChange={setTimeUnit}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">Stunden</SelectItem>
                      <SelectItem value="minutes">Minuten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={calculateRate} className="w-full" disabled={!volumeForRate || !time}>
                Rate berechnen
              </Button>
              <ResultCard result={rateResult} title="Berechnete Infusionsrate" icon={Calculator} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="drops">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Droplets className="h-5 w-5 text-blue-600" />
                Tropfrate berechnen
              </CardTitle>
              <CardDescription>
                Berechnen Sie die Tropfen pro Minute für die manuelle Infusionseinstellung
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="volumeForDrops">Volumen (ml)</Label>
                  <Input
                    id="volumeForDrops"
                    type="number"
                    placeholder="z.B. 500"
                    value={volumeForDrops}
                    onChange={(e) => setVolumeForDrops(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeForDrops">Zeit (Stunden)</Label>
                  <Input
                    id="timeForDrops"
                    type="number"
                    placeholder="z.B. 4"
                    value={timeForDrops}
                    onChange={(e) => setTimeForDrops(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dropFactor">Tropffaktor</Label>
                  <Select value={dropFactor} onValueChange={setDropFactor}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 Tropfen/ml</SelectItem>
                      <SelectItem value="15">15 Tropfen/ml</SelectItem>
                      <SelectItem value="20">20 Tropfen/ml (Standard)</SelectItem>
                      <SelectItem value="60">60 Tropfen/ml (Mikrotropfer)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={calculateDropRate} className="w-full" disabled={!volumeForDrops || !timeForDrops}>
                Tropfrate berechnen
              </Button>
              <ResultCard result={dropResult} title="Berechnete Tropfrate" icon={Droplets} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="volume">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-blue-600" />
                Volumen berechnen
              </CardTitle>
              <CardDescription>
                Berechnen Sie das benötigte Volumen bei gegebener Rate und Zeit
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rateForVolume">Infusionsrate (ml/h)</Label>
                  <Input
                    id="rateForVolume"
                    type="number"
                    placeholder="z.B. 125"
                    value={rateForVolume}
                    onChange={(e) => setRateForVolume(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeForVolume">Zeit</Label>
                  <Input
                    id="timeForVolume"
                    type="number"
                    placeholder="z.B. 6"
                    value={timeForVolume}
                    onChange={(e) => setTimeForVolume(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeUnitForVolume">Zeiteinheit</Label>
                  <Select value={timeUnitForVolume} onValueChange={setTimeUnitForVolume}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">Stunden</SelectItem>
                      <SelectItem value="minutes">Minuten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={calculateVolume} className="w-full" disabled={!rateForVolume || !timeForVolume}>
                Volumen berechnen
              </Button>
              <ResultCard result={volumeResult} title="Berechnetes Volumen" icon={FlaskConical} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800">Wichtige Tropffaktoren</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="font-semibold text-blue-600">Standard</div>
              <div>20 Tropfen/ml</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="font-semibold text-blue-600">Makrotropfer</div>
              <div>10-15 Tropfen/ml</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="font-semibold text-blue-600">Mikrotropfer</div>
              <div>60 Tropfen/ml</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="font-semibold text-blue-600">Bluttransfusion</div>
              <div>10 Tropfen/ml</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};